import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sun, Wrench, Phone, Check, Loader2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function CustomerPortal() {
  const { qr } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", phone: "" });

  const load = () =>
    api.get(`/portal/${qr}`).then((r) => setData(r.data)).catch(() => setError("Invalid QR code"));

  useEffect(() => { load(); }, [qr]);

  const submit = async () => {
    if (!form.title || !form.description) { toast.error("Title and details required"); return; }
    await api.post(`/tickets`, {
      project_id: data.project.id,
      qr_code: qr,
      customer_name: data.project.customer_name,
      phone: form.phone,
      title: form.title,
      description: form.description,
      priority: "Medium",
    });
    toast.success("Ticket raised — we'll be in touch.");
    setOpen(false);
    setForm({ title: "", description: "", phone: "" });
    load();
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-center"><div><div className="label-eyebrow mb-2">404</div><div className="font-black text-2xl">{error}</div></div></div>;
  if (!data) return <div className="p-8 label-eyebrow">Loading…</div>;

  const p = data.project;
  const v2 = data.v2_summary || {};
  const customerStages = data.customer_stages || [];
  const pct = v2.completion_pct ?? p.completion_pct ?? 0;
  const currentLabel = data.current_milestone_label || v2.current_stage_label || p.current_stage || "";

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-[#0a0a0a] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_helios-platform-2/artifacts/zblm1o4j_Multimix%20Energy%20Pvt%20Ltd%20Logo%20%28150%20x%2050%20mm%29%20%281%29.png"
              alt="Multimix Energy"
              className="h-12 w-auto object-contain bg-white p-1"
            />
            <div className="label-eyebrow text-white/60 border-l border-white/20 pl-3">Customer<br/>Portal</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow text-white/60">QR</div>
            <div className="font-mono text-xs">{p.qr_code}</div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div className="flat-card p-8 text-center">
          <div className="w-16 h-16 bg-[#D1FAE5] mx-auto flex items-center justify-center mb-4">
            <Sun className="w-8 h-8 text-[#10B981]" strokeWidth={2} />
          </div>
          <div className="label-eyebrow">System Status</div>
          <h1 className="text-3xl font-black tracking-tighter mt-2" data-testid="portal-status-title">
            {pct >= 100 ? "System Live & Healthy" : `In Progress — ${pct}%`}
          </h1>
          <div className="text-sm text-[#4B5563] mt-2">Current stage: <b>{currentLabel}</b></div>
          <div className="mt-4 h-2 w-full bg-[#F3F4F6] max-w-md mx-auto">
            <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${pct}%` }} data-testid="portal-progress" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Tile label="Inverter Capacity" value={`${p.capacity_kw} kW`} />
          <Tile label="Panel Capacity" value={p.panel_capacity_kw ? `${p.panel_capacity_kw} kW` : "—"} />
          <Tile label="Warranty" value={`${p.warranty_years} years`} />
          <Tile label="Panel Brand" value={p.panel_brand || "—"} />
          <Tile label="Inverter SN" value={p.inverter_serial || "—"} mono />
        </div>

        <div className="flat-card p-6" data-testid="portal-milestones">
          <div className="label-eyebrow mb-4">Installation Progress · {customerStages.length} Milestones</div>
          <div className="space-y-3">
            {customerStages.map((s, i) => (
              <MilestoneTile key={i} index={i} stage={s} />
            ))}
            {customerStages.length === 0 && (
              <div className="text-xs text-[#9CA3AF]">No milestones available yet.</div>
            )}
          </div>
        </div>

        <div className="flat-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Need Help?</div>
              <div className="font-black text-xl tracking-tight">Raise a Service Ticket</div>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#EF4444] hover:bg-[#DC2626] uppercase tracking-widest text-xs font-bold" data-testid="portal-raise-ticket">
                  <Wrench className="w-3 h-3 mr-2" /> Raise Complaint
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#0a0a0a]">
                <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Tell us what's wrong</DialogTitle></DialogHeader>
                <div className="space-y-3 pt-2">
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short title" className="rounded-none" data-testid="portal-ticket-title" />
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue" rows={4} className="rounded-none" data-testid="portal-ticket-desc" />
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Your phone (for callback)" className="rounded-none" data-testid="portal-ticket-phone" />
                  <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="portal-ticket-submit">Submit Ticket</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {data.tickets.length > 0 ? (
            <div className="space-y-2">
              {data.tickets.map((t) => (
                <div key={t.id} className="border border-[#E5E7EB] p-3 text-sm flex justify-between">
                  <div>
                    <div className="font-bold">{t.title}</div>
                    <div className="text-xs text-[#4B5563]">{fmtDate(t.created_at)}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider font-bold">{t.status}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-[#9CA3AF]">No active tickets. Your system is in good shape.</div>}
        </div>

        <div className="text-center text-xs text-[#9CA3AF] pt-4">
          <Phone className="w-3 h-3 inline mr-1" /> Multimix Energy Pvt Ltd · KSEB Approved Vendor
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, mono }) {
  return (
    <div className="flat-card p-4">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`font-black tracking-tight text-lg ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}

function MilestoneTile({ index, stage }) {
  const status = stage.status || "pending";
  const isDone = status === "completed";
  const isInProgress = status === "in_progress";
  const Icon = isDone ? Check : isInProgress ? Loader2 : Circle;
  const color = isDone ? "#10B981" : isInProgress ? "#002FA7" : "#9CA3AF";
  const bg = isDone ? "#D1FAE5" : isInProgress ? "#DBEAFE" : "#F3F4F6";
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`portal-stage-${index}`}>
      <div className="w-8 h-8 flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className={`w-4 h-4 ${isInProgress ? "animate-spin" : ""}`} style={{ color }} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${isDone ? "text-[#0a0a0a]" : isInProgress ? "text-[#002FA7]" : "text-[#4B5563]"}`}>
          {String(index + 1).padStart(2, "0")} · {stage.label}
        </div>
        {isDone && stage.completed_at && (
          <div className="text-[10px] text-[#10B981] uppercase tracking-widest font-bold">
            Completed {fmtDate(stage.completed_at)}
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold shrink-0 px-2 py-1" style={{ background: bg, color }}>
        {status.replace("_", " ")}
      </div>
    </div>
  );
}
